import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
// Import JSZip for working with EPUB files (install with: npm install jszip)
import JSZip from "jszip";
// Import DOMParser polyfill for node environments (install with: npm install xmldom)
import { DOMParser } from "xmldom";
import { useNavigate } from "react-router-dom";

const DocumentUploader = ({ user }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [subject, setSubject] = useState("");
  const [gradeLevel, setGradeLevel] = useState("");
  const [contentLimit, setContentLimit] = useState(500000);
  const [processingMode, setProcessingMode] = useState("characters");

  const navigate = useNavigate();

  // Add this effect at the top of your existing useEffect blocks
  useEffect(() => {
    // Check if user is not admin and redirect
    if (!user.admin) {
      navigate("/");
    }
  }, [navigate]);
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  // Parse an EPUB file and extract text content
  const parseEpub = async (epubFile) => {
    try {
      // Read the EPUB file as an ArrayBuffer
      const content = await readFileAsArrayBuffer(epubFile);

      // Use JSZip to extract the EPUB contents
      const zip = new JSZip();
      const extracted = await zip.loadAsync(content);

      // Find the OPF file (contains the spine/reading order)
      let opfFile = null;
      let rootFolder = "";

      // First, try to find the container.xml which points to the OPF file
      if (extracted.files["META-INF/container.xml"]) {
        const containerXml =
          await extracted.files["META-INF/container.xml"].async("text");
        const parser = new DOMParser();
        const containerDoc = parser.parseFromString(
          containerXml,
          "application/xml",
        );
        const rootFileElement =
          containerDoc.getElementsByTagName("rootfile")[0];
        if (rootFileElement) {
          opfFile = rootFileElement.getAttribute("full-path");
          // Get the directory of the OPF file
          const lastSlash = opfFile.lastIndexOf("/");
          if (lastSlash !== -1) {
            rootFolder = opfFile.substring(0, lastSlash + 1);
          }
        }
      }

      if (!opfFile) {
        // Fallback: look for any .opf file
        const opfFiles = Object.keys(extracted.files).filter((file) =>
          file.endsWith(".opf"),
        );
        if (opfFiles.length > 0) {
          opfFile = opfFiles[0];
          const lastSlash = opfFile.lastIndexOf("/");
          if (lastSlash !== -1) {
            rootFolder = opfFile.substring(0, lastSlash + 1);
          }
        }
      }

      if (!opfFile) {
        throw new Error("Could not find the OPF file in the EPUB");
      }

      // Parse the OPF file to get the spine (reading order)
      const opfContent = await extracted.files[opfFile].async("text");
      const parser = new DOMParser();
      const opfDoc = parser.parseFromString(opfContent, "application/xml");

      // Get the manifest items (maps ids to file paths)
      const manifestItems = {};
      const manifestElements = opfDoc
        .getElementsByTagName("manifest")[0]
        .getElementsByTagName("item");
      for (let i = 0; i < manifestElements.length; i++) {
        const item = manifestElements[i];
        manifestItems[item.getAttribute("id")] = item.getAttribute("href");
      }

      // Get the spine items (reading order)
      const spineElements = opfDoc
        .getElementsByTagName("spine")[0]
        .getElementsByTagName("itemref");
      const spineIds = [];
      for (let i = 0; i < spineElements.length; i++) {
        spineIds.push(spineElements[i].getAttribute("idref"));
      }

      // Extract and concatenate text from all HTML files in the spine order
      let extractedText = "";
      let currentCharCount = 0;
      let filesProcessed = 0;

      for (const id of spineIds) {
        if (manifestItems[id]) {
          // Get the HTML file path
          const filePath = rootFolder + manifestItems[id];
          if (extracted.files[filePath]) {
            // Get the HTML content
            const htmlContent = await extracted.files[filePath].async("text");

            // Parse HTML and extract text
            const htmlDoc = parser.parseFromString(htmlContent, "text/html");
            let fileText = "";

            // Basic text extraction function - in a real app, you might need more sophisticated parsing
            function extractTextFromNode(node) {
              if (node.nodeType === 3) {
                // TEXT_NODE
                return node.nodeValue + " ";
              } else if (node.nodeType === 1) {
                // ELEMENT_NODE
                let text = "";
                for (let i = 0; i < node.childNodes.length; i++) {
                  text += extractTextFromNode(node.childNodes[i]);
                }

                // Add line breaks for certain elements
                if (
                  ["p", "div", "h1", "h2", "h3", "h4", "h5", "h6"].includes(
                    node.nodeName.toLowerCase(),
                  )
                ) {
                  text += "\n\n";
                }
                return text;
              }
              return "";
            }

            if (htmlDoc.getElementsByTagName("body").length > 0) {
              fileText = extractTextFromNode(
                htmlDoc.getElementsByTagName("body")[0],
              ).trim();
            }

            // Process based on selected mode
            if (processingMode === "files") {
              filesProcessed++;
              extractedText += fileText + "\n\n";

              if (filesProcessed >= contentLimit) {
                break;
              }
            } else {
              // characters mode
              if (currentCharCount + fileText.length > contentLimit) {
                // Only add text up to the limit
                extractedText += fileText.substring(
                  0,
                  contentLimit - currentCharCount,
                );
                break;
              } else {
                extractedText += fileText + "\n\n";
                currentCharCount += fileText.length;
              }
            }
          }
        }
      }

      return extractedText.trim();
    } catch (error) {
      console.error("Error parsing EPUB:", error);
      throw new Error(`Failed to parse EPUB: ${error.message}`);
    }
  };

  const readFileAsArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) =>
        reject(new Error("Failed to read file as ArrayBuffer"));
      reader.readAsArrayBuffer(file);
    });
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error("Failed to read file as text"));
      reader.readAsText(file);
    });
  };

  // Replace your handleUpload function with this improved version
  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let content;

      // Process file based on type
      if (file.name.toLowerCase().endsWith(".epub")) {
        content = await parseEpub(file);
      } else {
        // Text files - use the existing function
        content = await readFileAsText(file);
      }

      // Improved chunking that respects paragraph boundaries
      const paragraphs = content.split(/\n\s*\n/); // Split by empty lines
      const chunks = [];
      let currentChunk = "";
      let chunkSize = 1000; // You can adjust this

      for (const paragraph of paragraphs) {
        if (currentChunk.length + paragraph.length + 2 > chunkSize) {
          chunks.push(currentChunk);
          currentChunk = paragraph;
        } else {
          currentChunk += (currentChunk ? "\n\n" : "") + paragraph;
        }
      }

      if (currentChunk) {
        chunks.push(currentChunk);
      }

      // Create document objects with better metadata
      const documents = chunks.map((chunk, index) => ({
        text: chunk,
        source: `${file.name} (del ${index + 1})`,
        id: `${file.name.replace(/\.\w+$/, "")}-${index}`,
        position: index, // Add sequential position for ordering
      }));

      // Create metadata with more detailed information
      const metadata = {
        namespace: "nietzsche",
        book: file.name.replace(/\.\w+$/, ""),
        contentType: "book",
        totalChunks: chunks.length,
        fileName: file.name,
      };

      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

      // Upload to API
      const response = await axios.post(`${API_URL}/api/documents/upload`, {
        documents,
        metadata,
      });

      setResult({
        fileName: file.name,
        chunks: chunks.length,
        message: response.data.message || "Uppladdningen lyckades",
      });
    } catch (err) {
      console.error("Upload error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Ett fel uppstod vid uppladdningen",
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Ladda upp läromedel
      </Typography>

      <Stack spacing={3} sx={{ mt: 3 }}>
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Metadata
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Ämne"
              variant="outlined"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="t.ex. matematik, svenska"
              size="small"
            />
            <TextField
              label="Årskurs"
              variant="outlined"
              fullWidth
              value={gradeLevel}
              onChange={(e) => setGradeLevel(e.target.value)}
              placeholder="t.ex. åk7, gymnasiet"
              size="small"
            />
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Innehållsbegränsning
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="processing-mode-label">
                Begränsningstyp
              </InputLabel>
              <Select
                labelId="processing-mode-label"
                value={processingMode}
                label="Begränsningstyp"
                onChange={(e) => setProcessingMode(e.target.value)}
              >
                <MenuItem value="characters">Antal tecken</MenuItem>
                <MenuItem value="files">Antal filer/kapitel</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={
                processingMode === "characters"
                  ? "Max antal tecken"
                  : "Max antal filer"
              }
              variant="outlined"
              fullWidth
              type="number"
              value={contentLimit}
              onChange={(e) =>
                setContentLimit(parseInt(e.target.value) || 5000)
              }
              size="small"
            />
          </Stack>
        </Box>

        <Box>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 2 }}
          >
            Välj fil
            <input
              type="file"
              hidden
              onChange={handleFileChange}
              accept=".txt,.md,.epub"
            />
          </Button>

          {file && (
            <Typography variant="body2" sx={{ ml: 1 }}>
              Vald fil: {file.name}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!file || loading}
          sx={{ mt: 2 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Ladda upp"
          )}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {result.message}
            </Alert>

            <List>
              <ListItem>
                <ListItemText primary="Fil" secondary={result.fileName} />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Antal textstycken"
                  secondary={result.chunks}
                />
              </ListItem>
            </List>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default DocumentUploader;
